import { Dispatch } from "redux";
import { toast } from "react-toastify";
import { appendUserRole, removeUserRole } from './reducer'
import Role from "models/role";
import User, { IUserQueryParams } from "models/user";
import UserStatus from "models/user-status";
import UserFields from "Components/organisms/forms/user-form/types";
import { PaginatedResult } from "models/queryable";
import { buildQueryString } from "utils/query-string";

export const getUsers = async (query?: IUserQueryParams): Promise<PaginatedResult<User> | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/user${buildQueryString(query)}`

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data as PaginatedResult<User>;

    } catch (err: any) {
        toast.error(err.message ?? "Error getting users");
        return null;
    }
}


export const getUserFormFields = async (): Promise<{
    userStatuses: UserStatus[];
} | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/user/form-fields`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error getting user form fields");
        return null;
    }
}

export interface EmployeeMatch {
    id: string
    firstName: string
    lastName: string
    commonName: string | null
    companyEmail: string
    userId: string | null
}

export const getEmployeeMatch = async (email: string): Promise<EmployeeMatch | null | undefined> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/user/employee-match${buildQueryString({ email })}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data as EmployeeMatch | null;
    } catch (err: any) {
        toast.error(err.message ?? "Error looking up employee");
        // undefined = lookup failed, null = no matching employee
        return undefined;
    }
}

export const createUser = async (email: string, password: string): Promise<User | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/auth/register`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({ email, password }),
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!res.ok) {
            // Register sends some errors as plain text, others as { message }
            const errText = await res.text();
            let message = errText;
            try { message = JSON.parse(errText).message ?? errText } catch { }
            throw new Error(message);
        }

        toast.success("User created.");
        const data = await res.json();
        return data.data as User;
    } catch (err: any) {
        toast.error(err.message || "Error creating user");
        return null;
    }
}

export const resetUserPassword = async (
    userId: string,
    password: string,
    sendEmail: boolean
): Promise<boolean> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/user/${userId}/reset-password`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({ password, sendEmail }),
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success(sendEmail ? "Password reset and emailed to user." : "Password reset.");
        return true;
    } catch (err: any) {
        toast.error(err.message ?? "Error resetting password");
        return false;
    }
}

export const submitEditUserForm = async (
    userId: string,
    vals: UserFields
): Promise<User | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/user/${userId}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'PATCH',
            body: JSON.stringify(vals),
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("User updated.");
        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error editing user");
        return null;
    }
}

export const terminateUser = async (userId: string): Promise<User | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/user/${userId}/terminate`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'POST',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("User terminated.");
        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error terminating user");
        return null;
    }
}

/**
 * Assign multiple roles to a user.
 * 
 * @param roles Roles to assign
 * @param u User to assign the roles to
 */
export const assignRoles = (roles: Role[], u: User) => async (dispatch: Dispatch): Promise<boolean> => {
    try {
        const resRoles: Role[] = [];
        for (const r of roles) {
            // Construct API endpoint to assign the role to the user
            const url = `${process.env.REACT_APP_API_URL}/roles/assign/${r.id}/${u.id}`

            // Make POST request to backend
            const res = await fetch(url, {
                method: 'POST',
                credentials: 'include'
            });

            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }

            // Update Redux state to immediately reflect the new role
            resRoles.push(r);
        }
        dispatch(appendUserRole({ roles: resRoles, user: u }))

        return true;
    } catch (err: any) {
        console.error('Error assigning roles:', err);
        return false;
    }
}

/**
 * Revoke multiple roles from a user.
 * 
 * @param roles Roles to revoke
 * @param u User to remove the roles from
 */
export const revokeRoles = (roles: Role[], u: User) => async (dispatch: Dispatch): Promise<boolean> => {
    try {
        const resRoles: Role[] = [];
        for (const r of roles) {
            // Construct API endpoint to revoke the role from the user
            const url = `${process.env.REACT_APP_API_URL}/roles/revoke/${r.id}/${u.id}`

            // Make DELETE request to backend
            const res = await fetch(url, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }

            resRoles.push(r);
        }
        dispatch(removeUserRole({ roles: resRoles, user: u }))

        return true;
    } catch (err: any) {
        console.error('Error revoking roles:', err);
        return false;
    }
}

